import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { v4 } from 'uuid';

import { InlineFeedback } from '@eluve/blocks';
import { Stack, useToast } from '@eluve/components';
import { FeedbackTypeEnum } from '@eluve/graphql-types';
import { graphql } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

const userFeedbackFragment = graphql(`
  fragment UserFeedbackFragment on UserFeedback @_unmask {
    __typename
    id
    resourceType
    resourceId
    type
    comment
    rating
    createdAt
  }
`);

const insertUserFeedbackMutation = graphql(
  `
    mutation insertUserFeedback(
      $resourceType: String!
      $resourceId: String!
      $type: FeedbackTypeEnum!
      $comment: String
    ) {
      insertUserFeedbackOne(
        object: {
          resourceType: $resourceType
          resourceId: $resourceId
          type: $type
          comment: $comment
        }
      ) {
        ...UserFeedbackFragment
      }
    }
  `,
  [userFeedbackFragment],
);

const updateUserFeedbackMutation = graphql(
  `
    mutation updateUserFeedback(
      $tenantId: uuid!
      $id: uuid!
      $type: FeedbackTypeEnum
      $comment: String
    ) {
      updateUserFeedbackByPk(
        pkColumns: { tenantId: $tenantId, id: $id }
        _set: { type: $type, comment: $comment }
      ) {
        ...UserFeedbackFragment
      }
    }
  `,
  [userFeedbackFragment],
);

const getUserFeedbackQuery = graphql(
  `
    query getUserFeedback($resourceType: String!, $resourceId: String!) {
      userFeedback(
        where: {
          resourceType: { _eq: $resourceType }
          resourceId: { _eq: $resourceId }
        }
        limit: 1
      ) {
        ...UserFeedbackFragment
      }
    }
  `,
  [userFeedbackFragment],
);

interface ChatMessageFeedbackProps {
  messageId: string;
}

const CHAT_MESSAGE_RESOURCE_TYPE = 'CHAT_MESSAGE';

export const ChatMessageFeedback = ({
  messageId,
}: ChatMessageFeedbackProps) => {
  const tenantId = useAssignedTenantIdFromParams();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: feedbackData, loading } = useQuery(getUserFeedbackQuery, {
    variables: {
      resourceType: CHAT_MESSAGE_RESOURCE_TYPE,
      resourceId: messageId,
    },
  });

  const existingFeedback = feedbackData?.userFeedback?.[0];

  const [currentFeedbackId, setCurrentFeedbackId] = useState<string | null>(
    null,
  );
  const [currentFeedbackType, setCurrentFeedbackType] =
    useState<FeedbackTypeEnum | null>(null);
  const [currentIsFinalized, setCurrentIsFinalized] = useState(false);

  const feedbackId = existingFeedback?.id ?? currentFeedbackId;
  const feedbackType =
    (existingFeedback?.type as FeedbackTypeEnum | null) ?? currentFeedbackType;
  const isFinalized = Boolean(existingFeedback) || currentIsFinalized;

  const [insertFeedback] = useMutation(insertUserFeedbackMutation, {
    onCompleted: (data) => {
      const newId = data?.insertUserFeedbackOne?.id;
      if (newId) {
        setCurrentFeedbackId(newId);
      }
    },
    optimisticResponse: ({ type }) => ({
      insertUserFeedbackOne: {
        __typename: 'UserFeedback' as const,
        id: `temp-chat-feedback-${v4()}`,
        resourceType: CHAT_MESSAGE_RESOURCE_TYPE,
        resourceId: messageId,
        type: type,
        comment: null,
        rating: null,
        createdAt: new Date().toISOString(),
      },
    }),
    onError: () => toast.error('Failed to send feedback'),
  });

  const [updateFeedback] = useMutation(updateUserFeedbackMutation, {
    optimisticResponse: ({ id, type, comment }) => {
      if (!type) {
        return { updateUserFeedbackByPk: null };
      }
      return {
        updateUserFeedbackByPk: {
          __typename: 'UserFeedback' as const,
          id,
          resourceType: CHAT_MESSAGE_RESOURCE_TYPE,
          resourceId: messageId,
          type,
          comment: comment ?? null,
          rating: null,
          createdAt: new Date().toISOString(),
        },
      };
    },
    onError: () => toast.error('Failed to update feedback'),
  });

  const handleThumbsClick = async (type: FeedbackTypeEnum) => {
    if (isFinalized || loading) {
      return;
    }
    setCurrentFeedbackType(type);
    try {
      setIsSubmitting(true);
      if (feedbackId) {
        await updateFeedback({ variables: { tenantId, id: feedbackId, type } });
      } else {
        await insertFeedback({
          variables: {
            resourceType: CHAT_MESSAGE_RESOURCE_TYPE,
            resourceId: messageId,
            type,
          },
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCommentSubmit = async (comment: string) => {
    if (!feedbackId || !feedbackType || isFinalized || loading) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await updateFeedback({
        variables: {
          tenantId,
          id: feedbackId,
          type: feedbackType,
          comment,
        },
      });

      if (result?.data?.updateUserFeedbackByPk?.comment === comment) {
        const message =
          feedbackType === 'POSITIVE'
            ? 'Thanks for the feedback! Glad you liked this response!'
            : 'Sorry that this response missed the mark. Every piece of feedback helps us improve future results.';

        toast.success(message);
        setCurrentIsFinalized(true);
      }
    } catch {
      toast.error('Failed to save comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isFinalized || loading) {
      return;
    }
    setCurrentIsFinalized(true);
  };

  return (
    <Stack gap={2} align="stretch">
      <InlineFeedback
        justify="start"
        sectionLabel={`Was this response helpful?`}
        feedbackType={feedbackType}
        isSubmitting={isSubmitting || loading}
        isFinalized={isFinalized}
        onThumbsClick={handleThumbsClick}
        onCommentSubmit={handleCommentSubmit}
        onClose={handleClose}
      />
    </Stack>
  );
};
